import type { ICartItem } from '$lib/components/cart/store.svelte';

export class GoogleAnalyticsProvider {
	protected currency: string;

	constructor(currency: string) {
		this.currency = currency;
	}

	#trackEvent(eventName: string, eventParams: Record<string, unknown> = {}) {
		gtag('event', eventName, eventParams);
	}

	trackViewItem(product: Product, variant: Variant) {
		const price = variant.special_price ?? variant.price!;
		const discount = variant.price! - price;
		this.#trackEvent('view_item', {
			currency: this.currency,
			value: price,
			items: [
				{
					item_id: product.data.sku,
					item_name: product.data.name,
					item_brand: product.data.brand.name,
					price: price,
					quantity: product.data.quantity_per_unit ?? 1,
					discount: discount,
				},
			],
		});
	}

	trackAddToCart(product: Product, variant: Variant, quantity: number) {
		const price = variant.special_price ?? variant.price!;
		const discount = variant.price! - price;
		this.#trackEvent('add_to_cart', {
			currency: this.currency,
			value: price * quantity,
			items: [
				{
					item_id: variant.data.sku,
					item_name: product.data.name,
					item_brand: product.data.brand.name,
					item_variant: variant.data.size,
					price: price,
					quantity: quantity,
					discount: discount,
				},
			],
		});
	}

	trackBeginCheckout(items: ICartItem[], totalPrice: number, couponCode?: string) {
		this.#trackEvent('begin_checkout', {
			currency: this.currency,
			value: totalPrice,
			coupon: couponCode,
			items: items.map((item) => ({
				item_id: item.sku,
				item_name: item.name,
				item_brand: item.brand,
				item_variant: item.variation,
				price: item.unit_price,
				quantity: item.quantity,
				discount: item.discount_amount,
			})),
		});
	}

	trackPurchase(order: SalesOrder) {
		this.#trackEvent('purchase', {
			currency: this.currency,
			value:
				order.grand_total - order.shipping_amount - order.tax_amount + order.shipping_tax_amount,
			transaction_id: order.ref,
			coupon: order.coupon_code,
			shipping: order.shipping_amount,
			tax: order.tax_amount,
			items: order.items.map((item) => ({
				item_id: item.sku,
				item_name: item.name,
				item_brand: item.brand,
				item_variant: item.variation,
				price: item.unit_price,
				quantity: item.quantity,
				discount: item.discount_amount,
			})),
		});
	}
}

export default GoogleAnalyticsProvider;
